import * as React from 'react'
import PropTypes from 'prop-types'
import { format } from 'date-fns'
import { Calendar as CalendarIcon } from 'lucide-react'

import { Button } from './button'
import { Input } from './input'
import { Calendar } from './calendar'
import { Popover, PopoverContent, PopoverTrigger } from './popover'

function parseDateValue(value) {
  if (value instanceof Date && !isNaN(value.getTime())) return value
  if (typeof value === 'string' && value) {
    const [dateOnly] = value.split('T')
    const parts = dateOnly.split('-')
    if (parts.length === 3) {
      const [year, month, day] = parts.map(Number)
      if (!isNaN(year) && !isNaN(month) && !isNaN(day)) {
        const parsed = new Date(year, month - 1, day)
        if (!isNaN(parsed.getTime())) return parsed
      }
    }
  }
  return undefined
}

function parseTypedDate(value, fromYear, toYear) {
  const trimmedValue = value.trim()
  const match = trimmedValue.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/) || trimmedValue.match(/^(\d{2})(\d{2})(\d{4})$/)
  if (!match) return null

  const [, monthString, dayString, yearString] = match
  const month = Number(monthString)
  const day = Number(dayString)
  const year = Number(yearString)
  const parsed = new Date(year, month - 1, day)

  if (
    parsed.getFullYear() !== year ||
    parsed.getMonth() !== month - 1 ||
    parsed.getDate() !== day ||
    year < fromYear ||
    year > toYear
  ) {
    return null
  }

  return parsed
}

const DatePicker = React.forwardRef(
  (
    {
      value,
      onChange,
      placeholder = 'MM/DD/YYYY',
      className = '',
      error,
      fromYear = 1900,
      toYear = new Date().getFullYear()
    },
    ref
  ) => {
    const [inputValue, setInputValue] = React.useState('')
    const isEditing = React.useRef(false)
    const [open, setOpen] = React.useState(false)
    const date = React.useMemo(() => parseDateValue(value), [value])
    const typedDate = React.useMemo(() => parseTypedDate(inputValue, fromYear, toYear), [inputValue, fromYear, toYear])
    const calendarDate = date || typedDate

    React.useEffect(() => {
      if (!isEditing.current) {
        setInputValue(date ? format(date, 'MM/dd/yyyy') : '')
      }
    }, [date])

    function handleInputChange(event) {
      const nextValue = event.target.value
      const parsed = parseTypedDate(nextValue, fromYear, toYear)
      setInputValue(parsed ? format(parsed, 'MM/dd/yyyy') : nextValue)

      if (!onChange) return

      onChange(parsed || '')
    }

    function handleInputBlur() {
      const parsed = parseTypedDate(inputValue, fromYear, toYear)
      isEditing.current = false
      if (parsed) {
        setInputValue(format(parsed, 'MM/dd/yyyy'))
      }
    }

    function handleSelect(selectedDate) {
      if (onChange) {
        onChange(selectedDate)
      }
      if (selectedDate) {
        setInputValue(format(selectedDate, 'MM/dd/yyyy'))
        setOpen(false)
      }
    }

    return (
      <div className={`space-y-1 ${className}`}>
        <div className="flex">
          <Input
            ref={ref}
            type="text"
            inputMode="numeric"
            placeholder={placeholder}
            value={inputValue}
            onFocus={() => {
              isEditing.current = true
            }}
            onChange={handleInputChange}
            onBlur={handleInputBlur}
            aria-invalid={Boolean(error)}
            className={`rounded-r-none ${error ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
          />
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="outline"
                size="icon"
                aria-label="Open calendar"
                className={`rounded-l-none border-l-0 ${error ? 'border-red-500' : ''}`}
              >
                <CalendarIcon className="h-4 w-4" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={calendarDate}
                onSelect={handleSelect}
                defaultMonth={calendarDate || new Date(toYear, 0)}
                fromYear={fromYear}
                toYear={toYear}
              />
            </PopoverContent>
          </Popover>
        </div>
        {error && <p className="text-sm text-red-600">{error}</p>}
      </div>
    )
  }
)
DatePicker.displayName = 'DatePicker'

DatePicker.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  className: PropTypes.string,
  error: PropTypes.string,
  fromYear: PropTypes.number,
  toYear: PropTypes.number
}

export { DatePicker }
